import { useVirtualizer } from '@tanstack/react-virtual';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import type { PagedItems } from './paged-items';

export interface VirtualListProps<T> {
  items: PagedItems<T>;
  rowHeight: number;
  renderRow: (item: T | undefined, index: number) => ReactNode;
  className?: string;
  overscan?: number;
}

/** Vertical list that mounts only visible rows and fetches their pages. */
export function VirtualList<T>({ items, rowHeight, renderRow, className, overscan = 8 }: VirtualListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.total ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan,
  });
  const range = virtualizer.getVirtualItems();
  const first = range[0]?.index ?? 0;
  const last = range[range.length - 1]?.index ?? 0;
  useEffect(() => items.ensure(first, last), [items, first, last]);

  return (
    <div ref={parentRef} className={`virtual-scroll ${className ?? ''}`}>
      <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
        {range.map((row) => (
          <div key={row.key} className="virtual-row" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: rowHeight, transform: `translateY(${row.start}px)` }}>
            {renderRow(items.get(row.index), row.index)}
          </div>
        ))}
      </div>
    </div>
  );
}
